// Формат «экрана» Формы (мини-аппа) Scriptera — ЕДИНСТВЕННЫЙ источник истины.
// Экран отдают узлы «Форма» (nodes/ScripteraForm) и «Экран» (nodes/ScripteraScreen),
// рисует наш рендерер на form.scriptera.ai (apps/web импортирует этот же модуль).
// Файл сознательно без зависимостей и без импортов n8n: компилируется в dist
// и используется по обе стороны HTTP. Карта: docs/HOW-IT-WORKS.md §15.
//
// Модель (решения владельца 2026-07-15):
// - экран = заголовок/текст/картинка/видео + поля ввода + кнопки;
// - кнопки kind:'next' НЕ являются выходами узла (движок n8n отдаёт продолжение
//   только в выход 0 — решение владельца 2026-07-15): у узлов один выход, нажатая
//   кнопка приходит полем button, ветвление — штатными «Если»/«Свитч» следом;
//   нет ни одной next-кнопки → экран финальный: узел НЕ замирает, выполнение
//   завершается;
// - тема (цвет/лого/фон) — НЕ здесь: свойство формы целиком, живёт в БД Scriptera;
// - значение поля-файла — строка-URL, который выдал наш прокси при загрузке
//   (узлы и сценарий бинарь не таскают);
// - файлы сценария («скачать сертификат») — кнопка download с file:<имя вложения
//   входных данных>; узел «Экран» вкладывает файл base64 в files ответа, прокси
//   перекладывает его в наше хранилище и подменяет кнопку ссылкой нашего домена
//   (§8 решений: голых ссылок на инстанс не бывает). Браузер files не видит.

export const SCREEN_FORMAT_VERSION = 1;

/** Ограничения формата — защита рендерера и канона от мусора. */
export const SCREEN_LIMITS = {
  maxTitle: 200,
  maxText: 4000,
  maxLabel: 200,
  maxPlaceholder: 200,
  maxUrl: 2000,
  maxFields: 20,
  maxButtons: 8,
  maxChoiceOptions: 30,
} as const;

/** Кап файла формы (и загрузка поля-файла, и «скачать» из сценария), байт исходника. */
export const SCREEN_FILE_MAX_BYTES = 10 * 1024 * 1024;

/** Ошибка «на экране ничего нет» — карточки кабинета отличают её от настоящего брака
 *  (пустой новый шаг показывает дружелюбный образец, а не ошибку). */
export const SCREEN_EMPTY_ERROR =
  'экран пуст: нужен заголовок, текст, картинка, видео, поле или кнопка';

/** key поля / id кнопки: латиница+цифры+подчёркивание, 1–64. */
export const SCREEN_KEY_RE = /^[a-zA-Z0-9_]{1,64}$/;

export interface ScreenImage {
  url: string;
  alt?: string;
}

/** Видео на экране: ссылка на файл (наше хранилище или внешний адрес), плеер рисует
 *  рендерер. Постера нет намеренно — первый кадр берёт сам браузер. */
export interface ScreenVideo {
  url: string;
}

interface ScreenFieldBase {
  /** Ключ значения в ответе экрана (fields[key]). */
  key: string;
  label: string;
  required?: boolean;
  placeholder?: string;
}

export interface ScreenTextField extends ScreenFieldBase {
  kind: 'text';
  multiline?: boolean;
}

export interface ScreenChoiceOption {
  value: string;
  label: string;
}

/** Выбор одного из вариантов; радио или выпадашка — решает рендерер по числу опций. */
export interface ScreenChoiceField extends ScreenFieldBase {
  kind: 'choice';
  options: ScreenChoiceOption[];
}

export interface ScreenEmailField extends ScreenFieldBase {
  kind: 'email';
}

export interface ScreenPhoneField extends ScreenFieldBase {
  kind: 'phone';
}

export interface ScreenDateField extends ScreenFieldBase {
  kind: 'date';
}

export interface ScreenFileField extends ScreenFieldBase {
  kind: 'file';
  /** Как accept у <input type=file>: '.pdf,image/*'. */
  accept?: string;
}

export type ScreenField =
  | ScreenTextField
  | ScreenChoiceField
  | ScreenEmailField
  | ScreenPhoneField
  | ScreenDateField
  | ScreenFileField;

export const SCREEN_FIELD_KINDS = ['text', 'choice', 'email', 'phone', 'date', 'file'] as const;

/** Кнопка-продолжение: нажатие уходит в сценарий полем button (ветвит «Если»/«Свитч» после узла). */
export interface ScreenNextButton {
  kind: 'next';
  id: string;
  label: string;
  /** Акцентная (главная) кнопка экрана. */
  primary?: boolean;
}

/** Кнопка-ссылка: открыть URL (оплата и т.п.), в сценарий ничего не уходит. */
export interface ScreenLinkButton {
  kind: 'link';
  label: string;
  url: string;
  primary?: boolean;
}

/**
 * Кнопка «скачать файл» — ровно одно из двух:
 * - url: готовая http(s)-ссылка (внешний файл);
 * - file: имя вложения входных данных узла «Экран» — узел вложит файл в ответ,
 *   прокси переложит в наше хранилище и подменит кнопку ссылкой нашего домена.
 *   На первом экране (триггер «Форма») file недоступен: сценарий ещё не запускался.
 */
export interface ScreenDownloadButton {
  kind: 'download';
  label: string;
  url?: string;
  file?: string;
}

export type ScreenButton = ScreenNextButton | ScreenLinkButton | ScreenDownloadButton;

export const SCREEN_BUTTON_KINDS = ['next', 'link', 'download'] as const;

/** Экран — то, что узел отдаёт браузеру, а рендерер рисует. */
export interface FormScreen {
  v: number;
  title?: string;
  /** Простой текст; пустая строка между абзацами = новый абзац. Без HTML. */
  text?: string;
  image?: ScreenImage;
  video?: ScreenVideo;
  fields?: ScreenField[];
  buttons?: ScreenButton[];
}

/** Ответ браузера на экран (POST рендерера в сторону узла). */
export interface ScreenAnswer {
  /** id нажатой next-кнопки. */
  button: string;
  /** Значения полей экрана; файл = URL, выданный прокси при загрузке. */
  fields: Record<string, string>;
}

/**
 * Что узлы отдают в сценарий при нажатии (выход триггера «Форма» и узла «Экран»).
 * link/client заполняет только триггер (первое нажатие).
 */
export interface ScreenSubmitPayload {
  /** ID одного прохождения формы; постоянного ID пользователя в браузере НЕТ. */
  sessionId: string;
  button: string;
  fields: Record<string, string>;
  /** Query-параметры ссылки формы (utm_*, свои метки). */
  link?: Record<string, string>;
  client?: { lang?: string; mobile?: boolean };
}

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

const isShortString = (v: unknown, max: number): v is string =>
  typeof v === 'string' && v.length > 0 && v.length <= max;

/** http(s)-URL в пределах лимита. */
const isHttpUrl = (v: unknown): v is string =>
  isShortString(v, SCREEN_LIMITS.maxUrl) && /^https?:\/\//.test(v);

// Тексты ошибок читает КЛИЕНТ в кабинете («Что доделать») — человеческие фразы,
// имена полей как в конструкторе («Подпись», «Имя в данных»), без kind/label/key.
function validateField(field: unknown, index: number, keys: Set<string>, errors: string[]): void {
  const at = `поле ${index + 1}`;
  if (!isRecord(field)) {
    errors.push(`${at}: запись сломана — удали и добавь поле заново`);
    return;
  }
  if (!SCREEN_FIELD_KINDS.includes(field.kind as (typeof SCREEN_FIELD_KINDS)[number])) {
    errors.push(`${at}: неизвестный тип поля`);
    return;
  }
  if (typeof field.key !== 'string' || !SCREEN_KEY_RE.test(field.key)) {
    errors.push(`${at}: «Имя в данных» — латиница/цифры/подчёркивание, 1–64 символа`);
  } else if (keys.has(field.key)) {
    errors.push(`${at}: «Имя в данных» ${field.key} уже занято другим полем`);
  } else {
    keys.add(field.key);
  }
  if (!isShortString(field.label, SCREEN_LIMITS.maxLabel)) {
    errors.push(`${at}: не заполнена «Подпись» (текст до ${SCREEN_LIMITS.maxLabel} символов)`);
  }
  if (field.placeholder !== undefined && !isShortString(field.placeholder, SCREEN_LIMITS.maxPlaceholder)) {
    errors.push(`${at}: «Плейсхолдер» — текст до ${SCREEN_LIMITS.maxPlaceholder} символов`);
  }
  if (field.kind === 'choice') {
    const options = field.options;
    if (!Array.isArray(options) || options.length === 0 || options.length > SCREEN_LIMITS.maxChoiceOptions) {
      errors.push(`${at}: «Варианты» — от 1 до ${SCREEN_LIMITS.maxChoiceOptions}`);
    } else {
      const values = new Set<string>();
      for (const [j, option] of options.entries()) {
        if (
          !isRecord(option) ||
          typeof option.value !== 'string' ||
          !SCREEN_KEY_RE.test(option.value) ||
          !isShortString(option.label, SCREEN_LIMITS.maxLabel)
        ) {
          errors.push(`${at}, вариант ${j + 1}: нужны значение (латиница/цифры/_) и надпись`);
        } else if (values.has(option.value)) {
          errors.push(`${at}, вариант ${j + 1}: значение ${option.value} повторяется`);
        } else {
          values.add(option.value);
        }
      }
    }
  }
  if (field.kind === 'file' && field.accept !== undefined && !isShortString(field.accept, SCREEN_LIMITS.maxPlaceholder)) {
    errors.push(`${at}: «Какие файлы» — строка до ${SCREEN_LIMITS.maxPlaceholder} символов`);
  }
}

function validateButton(button: unknown, index: number, ids: Set<string>, errors: string[]): void {
  const at = `кнопка ${index + 1}`;
  if (!isRecord(button)) {
    errors.push(`${at}: запись сломана — удали и добавь кнопку заново`);
    return;
  }
  if (!SCREEN_BUTTON_KINDS.includes(button.kind as (typeof SCREEN_BUTTON_KINDS)[number])) {
    errors.push(`${at}: неизвестное действие кнопки`);
    return;
  }
  if (!isShortString(button.label, SCREEN_LIMITS.maxLabel)) {
    errors.push(`${at}: не заполнена «Надпись» (текст до ${SCREEN_LIMITS.maxLabel} символов)`);
  }
  if (button.kind === 'next') {
    if (typeof button.id !== 'string' || !SCREEN_KEY_RE.test(button.id)) {
      errors.push(`${at}: «Имя в данных» — латиница/цифры/подчёркивание, 1–64 символа`);
    } else if (ids.has(button.id)) {
      errors.push(`${at}: «Имя в данных» ${button.id} уже занято другой кнопкой`);
    } else {
      ids.add(button.id);
    }
  }
  if (button.kind === 'link' && !isHttpUrl(button.url)) {
    errors.push(`${at}: «Ссылка» — адрес http(s) до ${SCREEN_LIMITS.maxUrl} символов`);
  }
  if (button.kind === 'download') {
    const hasUrl = button.url !== undefined;
    const hasFile = button.file !== undefined;
    if (hasUrl === hasFile) {
      errors.push(`${at}: выбери одно — внешняя ссылка или файл из входных данных`);
    } else if (hasUrl && !isHttpUrl(button.url)) {
      errors.push(`${at}: «Ссылка» — адрес http(s) до ${SCREEN_LIMITS.maxUrl} символов`);
    } else if (hasFile && (typeof button.file !== 'string' || !SCREEN_KEY_RE.test(button.file))) {
      errors.push(`${at}: «Файл» — имя вложения (латиница/цифры/подчёркивание, 1–64)`);
    }
  }
}

/**
 * Проверка экрана. Пустой массив = экран валиден.
 * Требование «на экране есть хоть что-то» тоже здесь: совсем пустой экран — ошибка.
 */
export function validateFormScreen(value: unknown): string[] {
  const errors: string[] = [];
  if (!isRecord(value)) return ['экран сломан — пересоздай шаг'];
  if (value.v !== SCREEN_FORMAT_VERSION) {
    errors.push(`поддерживается только версия экрана ${SCREEN_FORMAT_VERSION}`);
  }
  if (value.title !== undefined && !isShortString(value.title, SCREEN_LIMITS.maxTitle)) {
    errors.push(`«Заголовок» — текст до ${SCREEN_LIMITS.maxTitle} символов`);
  }
  if (value.text !== undefined && !isShortString(value.text, SCREEN_LIMITS.maxText)) {
    errors.push(`«Текст» — до ${SCREEN_LIMITS.maxText} символов`);
  }
  if (value.image !== undefined) {
    if (!isRecord(value.image) || !isHttpUrl(value.image.url)) {
      errors.push('«Картинка»: нужна ссылка http(s)');
    } else if (value.image.alt !== undefined && !isShortString(value.image.alt, SCREEN_LIMITS.maxLabel)) {
      errors.push(`«Картинка», подпись — текст до ${SCREEN_LIMITS.maxLabel} символов`);
    }
  }
  if (value.video !== undefined && (!isRecord(value.video) || !isHttpUrl(value.video.url))) {
    errors.push('«Видео»: нужна ссылка http(s)');
  }
  const fields = value.fields ?? [];
  if (!Array.isArray(fields) || fields.length > SCREEN_LIMITS.maxFields) {
    errors.push(`полей на экране — не больше ${SCREEN_LIMITS.maxFields}`);
  } else {
    const keys = new Set<string>();
    fields.forEach((field, i) => validateField(field, i, keys, errors));
  }
  const buttons = value.buttons ?? [];
  if (!Array.isArray(buttons) || buttons.length > SCREEN_LIMITS.maxButtons) {
    errors.push(`кнопок на экране — не больше ${SCREEN_LIMITS.maxButtons}`);
  } else {
    const ids = new Set<string>();
    buttons.forEach((button, i) => validateButton(button, i, ids, errors));
  }
  if (
    errors.length === 0 &&
    value.title === undefined &&
    value.text === undefined &&
    value.image === undefined &&
    // Экран из одного видео — законный экран (ролик-приветствие без слов).
    value.video === undefined &&
    (fields as unknown[]).length === 0 &&
    (buttons as unknown[]).length === 0
  ) {
    errors.push(SCREEN_EMPTY_ERROR);
  }
  return errors;
}

export function isFormScreen(value: unknown): value is FormScreen {
  return validateFormScreen(value).length === 0;
}

/** next-кнопки экрана (валидация нажатий; выход у узла один — см. шапку). */
export function screenNextButtons(screen: FormScreen): ScreenNextButton[] {
  return (screen.buttons ?? []).filter((b): b is ScreenNextButton => b.kind === 'next');
}

/** Финальный экран — без next-кнопок: узел не замирает, выполнение завершается. */
export function isFinalScreen(screen: FormScreen): boolean {
  return screenNextButtons(screen).length === 0;
}

/** download-кнопки с file — файлы, которые узел «Экран» обязан вложить в ответ. */
export function screenFileButtons(screen: FormScreen): ScreenDownloadButton[] {
  return (screen.buttons ?? []).filter(
    (b): b is ScreenDownloadButton => b.kind === 'download' && b.file !== undefined,
  );
}

/** Разбор значения json-параметра узла (строка или уже объект) в валидный экран. */
export function parseScreenJson(raw: unknown): { screen?: FormScreen; errors: string[] } {
  let value: unknown = raw;
  if (typeof raw === 'string') {
    try {
      value = JSON.parse(raw);
    } catch {
      return { errors: ['экран: параметр не является корректным JSON'] };
    }
  }
  const errors = validateFormScreen(value);
  return errors.length ? { errors } : { screen: value as FormScreen, errors };
}

// ── Экран из СТРУКТУРНЫХ параметров узла (HOW-IT-WORKS §15) ─────────────────────────
// Узлы «Форма»/«Экран» описывают экран полями формата n8n (lib/screen-params.ts):
// title/text/image + fixedCollection fields/buttons — конструктор в кабинете рисует
// штатный движок формы шага. Здесь единственный маппинг «значения параметров → FormScreen»;
// им живут узлы (рантайм), снимок первого экрана (packages/ui workflow.ts) и
// предпросмотр в кабинете. Легаси-параметр screen (json-строка первых сценариев)
// поддерживается как фолбэк — старые workflow продолжают работать.

/** Элементы fixedCollection n8n: { items: [...] }. */
function fcItems(v: unknown): Record<string, unknown>[] {
  if (!isRecord(v) || !Array.isArray(v.items)) return [];
  return v.items.filter(isRecord);
}

/** Непустая строка (обрезки не делаем — пробелы внутри текста легальны). */
const nonEmpty = (v: unknown): string | undefined =>
  typeof v === 'string' && v.trim() !== '' ? v : undefined;

function fieldFromItem(item: Record<string, unknown>): Record<string, unknown> {
  const kind = typeof item.kind === 'string' ? item.kind : 'text';
  const out: Record<string, unknown> = { kind, key: item.key, label: item.label };
  if (item.required === true) out.required = true;
  const placeholder = nonEmpty(item.placeholder);
  if (placeholder && (kind === 'text' || kind === 'email' || kind === 'phone')) {
    out.placeholder = placeholder;
  }
  if (kind === 'text' && item.multiline === true) out.multiline = true;
  if (kind === 'choice') {
    out.options = fcItems(item.options).map((o) => ({ value: o.value, label: o.label }));
  }
  const accept = nonEmpty(item.accept);
  if (kind === 'file' && accept) out.accept = accept;
  return out;
}

function buttonFromItem(item: Record<string, unknown>): Record<string, unknown> {
  const kind = typeof item.kind === 'string' ? item.kind : 'next';
  if (kind === 'next') {
    return { kind, id: item.id, label: item.label, ...(item.primary === true ? { primary: true } : {}) };
  }
  if (kind === 'link') {
    return { kind, label: item.label, url: item.url, ...(item.primary === true ? { primary: true } : {}) };
  }
  // download: ровно одно из url|file — решает селектор source (у триггера «Форма»
  // источника нет: всегда url, вложений до запуска сценария не бывает).
  return item.source === 'file'
    ? { kind, label: item.label, file: item.file }
    : { kind, label: item.label, url: item.url };
}

/**
 * Экран из параметров узла: структурные поля (screen-params) → FormScreen с валидацией.
 * Структурные параметры главнее; ПУСТЫЕ структурные + легаси-параметр screen (json,
 * hidden в описании узла) → разбор легаси: старые сценарии живут, а правка в
 * конструкторе сразу перекрывает старый json.
 */
export function screenFromNodeParams(params: Record<string, unknown>): {
  screen?: FormScreen;
  errors: string[];
} {
  const legacy = params.screen;
  const hasLegacy = (typeof legacy === 'string' && legacy.trim() !== '') || isRecord(legacy);
  const title = nonEmpty(params.title);
  const text = nonEmpty(params.text);
  const imageUrl = isRecord(params.image) ? nonEmpty(params.image.url) : undefined;
  const imageAlt = isRecord(params.image) ? nonEmpty(params.image.alt) : undefined;
  const videoUrl = isRecord(params.video) ? nonEmpty(params.video.url) : undefined;
  const fields = fcItems(params.fields).map(fieldFromItem);
  const buttons = fcItems(params.buttons).map(buttonFromItem);
  const value: Record<string, unknown> = {
    v: SCREEN_FORMAT_VERSION,
    ...(title ? { title } : {}),
    ...(text ? { text } : {}),
    ...(imageUrl ? { image: { url: imageUrl, ...(imageAlt ? { alt: imageAlt } : {}) } } : {}),
    ...(videoUrl ? { video: { url: videoUrl } } : {}),
    ...(fields.length > 0 ? { fields } : {}),
    ...(buttons.length > 0 ? { buttons } : {}),
  };
  const errors = validateFormScreen(value);
  if (errors.length === 1 && errors[0] === SCREEN_EMPTY_ERROR && hasLegacy) {
    return parseScreenJson(legacy);
  }
  return errors.length ? { errors } : { screen: value as unknown as FormScreen, errors };
}

/** Файл сценария в ответе узла (для download-кнопок с file): data — base64 исходника. */
export interface ScreenResponseFile {
  name: string;
  mime: string;
  data: string;
}

/**
 * Ответ на POST-нажатие (responseMode lastNode = вывод последнего узла):
 * следующий экран + адрес продолжения (form-waiting/<executionId>; null = финал,
 * выполнение завершено, продолжать некуда). files — вложения download-кнопок
 * (ключ = значение file кнопки); прокси перекладывает их в хранилище и до
 * браузера НЕ доносит.
 */
export interface ScreenStepResponse {
  screen: FormScreen;
  resume: string | null;
  files?: Record<string, ScreenResponseFile>;
}
